<template>
  <div class="flex w-full flex-col items-center pb-32">
    <div class="w-full items-center justify-start">
      <span class="text-5xl font-medium"> {{ headingText }}</span>
    </div>

    <CandidateShopCartProgressBar
      v-show="data.product.type === 'configurable'"
      size="size-6"
      :total-steps="2"
      :current-step="currentStep"
      class="mt-11"
    />
    <div
      v-if="currentStep === 1"
      class="mt-11 grid w-full grid-cols-1 gap-x-4 gap-y-8 rounded-lg bg-white px-6 py-8 shadow-md sm:grid-cols-2"
    >
      <ProductMediaForm :data="data" :errors="errors"></ProductMediaForm>

      <Input
        v-model="data.product.name"
        :value="data.product.name"
        :placeholder="__('Add item name')"
        :label="__('Name')"
        type="text"
        onkeydown=""
        class="w-full"
        :errors="errors?.name"
        required
      />

      <div class="col-span-1 w-full">
        <label class="mb-2 block text-sm text-[#58585E]"
          >{{ __('Product Category') }} *
        </label>
        <FormSingleselect
          v-model="data.product.category"
          :errors="errors?.['category.id']"
          :values="data.options.categories"
          required
          id="end_date_time"
          :placeholder="__('T-shirt')"
          :clearable="true"
          @clear="data.product.category = null"
          type="static"
        />
      </div>

      <div class="col-span-1 sm:col-span-2">
        <InputTextarea
          v-model:value="data.product.description"
          :errors="errors?.description"
          id="item_description"
          value=""
          :label="__('Description')"
          :placeholder="
            __('Here you can add more details about the product in general')
          "
        >
        </InputTextarea>
      </div>

      <div
        class="col-span-1 flex w-full flex-col items-start gap-y-2 sm:col-span-2"
      >
        <!-- All Radio -->

        <!-- Active Radio -->

        <label class="block text-sm text-[#58585E]"
          >{{ __('Product Delivery') }} *</label
        >
        <div class="flex w-full flex-row items-center justify-start gap-x-4">
          <label class="flex cursor-pointer items-center">
            <input
              type="radio"
              name="status"
              value="Online"
              :checked="data.product.delivery_type === 'online'"
              class="mr-2 cursor-pointer border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
              @change="handleChange('online')"
            />
            {{ __('Online') }}
          </label>

          <!-- Inactive Radio -->
          <label class="flex cursor-pointer items-center">
            <input
              type="radio"
              name="status"
              value="Offline"
              :checked="data.product.delivery_type === 'offline'"
              class="mr-2 cursor-pointer border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
              @change="handleChange('offline')"
            />
            {{ __('Offline') }}
          </label>
        </div>

        <p class="text-red-500" v-if="errors?.delivery_type">
          {{ errors?.delivery_type }}
        </p>
      </div>

      <div
        v-if="data.product.delivery_type === 'offline'"
        class="col-span-1 flex flex-col items-start justify-center gap-y-2 sm:col-span-2"
      >
        <div class="flex flex-row items-center justify-start gap-x-2">
          <span class="text-lg font-medium text-zinc-800">{{
            __('The product is available only in a certain location')
          }}</span>

          <div>
            <InputToggle
              id="is_location_restricted"
              :value="data.product.is_location_restricted"
              @update:value="handleLocationRestrictionChange"
              :errors="errors?.is_location_restricted"
            />
          </div>
        </div>
        <span v-if="errors?.locations" class="text-xs text-error">
          {{ errors?.locations[0] }}</span
        >
      </div>

      <ProductLocationForm
        :data="data"
        :errors="errors"
        v-if="
          data.product.is_location_restricted &&
          data.product.delivery_type === 'offline'
        "
      >
      </ProductLocationForm>

      <div
        v-if="data.product.delivery_type === 'offline'"
        class="col-span-1 flex flex-row items-center justify-start gap-x-2 sm:col-span-2"
      >
        <span class="text-lg font-medium text-zinc-800">{{
          __('The product is only available for pickup')
        }}</span>

        <div>
          <InputToggle
            id="is_pickup_only"
            :value="Boolean(data.product.is_pickup_only)"
            @update:value="
              data.product.is_pickup_only = !data.product.is_pickup_only
            "
            :errors="errors?.is_pickup_only"
          />
        </div>
      </div>

      <div v-if="data.product.is_pickup_only" class="col-span-1 sm:col-span-2">
        <Input
          v-model="data.product.pickup_address"
          :value="data.product.pickup_address"
          :placeholder="__('Add adress')"
          :label="__('Add pickup address')"
          type="text"
          onkeydown=""
          class="w-full"
          :errors="errors?.pickup_address"
          :icon="'location_on'"
        />
      </div>

      <div
        class="col-span-1 grid grid-cols-1 gap-y-4 sm:col-span-2 sm:grid-cols-3"
      >
        <div class="flex flex-col gap-y-2">
          <span> {{ __('Product Type') }}</span>
          <div class="flex h-10 w-full items-center">
            <button
              @click.prevent="handleStandaloneButtonClick"
              class="cursor-pointer rounded-l-md border px-6 py-2 transition-colors duration-200"
              :class="
                props.data.product.type === 'standalone'
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-zinc-300 bg-white text-black'
              "
            >
              <span class="text-sm">{{ __('Standalone') }} </span>
            </button>

            <button
              @click="handleConfigurableButtonClick"
              class="cursor-pointer rounded-r-md border px-6 py-2 transition-colors duration-200"
              :class="
                props.data.product.type === 'configurable'
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-zinc-300 bg-white text-black'
              "
            >
              <span class="text-sm"> {{ __('Configurable') }}</span>
            </button>
          </div>
        </div>

        <div class="flex flex-col gap-y-2">
          <span> {{ __('Product Visibility') }}</span>
          <div class="flex h-10 w-full items-center">
            <button
              @click.prevent="handleInternalButtonClick"
              class="cursor-pointer rounded-l-md border px-6 py-2 transition-colors duration-200"
              :class="
                data.product.external == 0 && data.product.internal == 1
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-zinc-300 bg-white text-black'
              "
            >
              <span class="text-sm">{{ __('Internal') }} </span>
            </button>

            <button
              @click="handleExternalButtonClick"
              class="cursor-pointer border px-6 py-2 transition-colors duration-200"
              :class="
                data.product.external == 1 && data.product.internal == 0
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-zinc-300 bg-white text-black'
              "
            >
              <span class="text-sm"> {{ __('External') }}</span>
            </button>

            <button
              @click="handleBothButtonClick"
              class="cursor-pointer rounded-r-md border px-6 py-2 transition-colors duration-200"
              :class="
                data.product.external == 1 && data.product.internal == 1
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-zinc-300 bg-white text-black'
              "
            >
              <span class="text-sm"> {{ __('Both') }}</span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="data.product.type === 'configurable'"
        class="col-span-1 rounded-lg border border-zinc-200 p-4 sm:col-span-2"
      >
        <div class="flex w-full flex-col items-start justify-center gap-y-2">
          <span class="text-sm font-medium"
            >{{ __('This product has a multiple versions.') }}
          </span>

          <span class="text-sm text-zinc-600"
            >{{ __("You'll configure all attributes in the next step") }}
          </span>
        </div>
      </div>

      <div
        v-if="data.product.type === 'standalone'"
        class="col-span-1 border p-4 sm:col-span-2"
      >
        <div class="flex w-full flex-col items-start justify-center gap-y-2">
          <span class="text-sm font-medium"
            >{{ __('This product has a single version.') }}
          </span>

          <span class="text-sm text-zinc-600"
            >{{
              __(
                'If you wish to add sizes or colors, set your product as configurable.'
              )
            }}
          </span>
        </div>
      </div>

      <div
        v-if="data.product.type === 'standalone'"
        class="col-span-1 sm:col-span-2"
      >
        <Input
          v-model="data.product.standalone_stock"
          :value="data.product.standalone_stock"
          class="col-span-2"
          :placeholder="__('Add stock')"
          :label="__('Available stock')"
          type="number"
          onkeydown=""
          :errors="errors?.standalone_stock"
          required
        />
      </div>

      <div class="col-span-1 sm:col-span-2">
        <Input
          v-model="data.product.max_quantity_per_user"
          :value="data.product.max_quantity_per_user"
          :placeholder="__('Add a maximum quantity')"
          :label="__('Maximum order quantity per user')"
          type="number"
          onkeydown=""
          class="w-full"
          :errors="errors?.max_quantity_per_user"
          :icon="'production_quantity_limits'"
        />
      </div>

      <div class="col-span-1 sm:col-span-2">
        <Input
          v-model="data.product.points"
          :value="data.product.points"
          :placeholder="__('Add points')"
          :label="__('Points')"
          type="number"
          onkeydown=""
          class="w-full"
          :errors="errors?.points"
          :icon="'jobful_points'"
          required
        />
      </div>

      <div
        v-for="(requirement, index) in props.data.product.requirements.filter(
          (req) => req.value != null
        )"
        class="col-span-1 flex flex-row"
      >
        <Input
          v-model="
            data.product.requirements.find((req) => req.id === requirement.id)
              .value
          "
          :value="
            data.product.requirements.find((req) => req.id === requirement.id)
              .value
          "
          :placeholder="__('Add required number of ') + requirement.name"
          :label="requirement.name"
          type="number"
          onkeydown=""
          class="w-full"
          :errors="errors?.['requirements.' + index + '.value']"
        />

        <div class="flex items-end justify-center">
          <button
            class="btn btn-ghost-no-hover gap-x-1.5"
            @click="
              props.data.product.requirements.find(
                (req) => req.id === requirement.id
              ).value = null
            "
          >
            <Icon class="text-xl text-primary" type="filled" name="delete" />
            <span>{{ __('Delete ') }}</span>
          </button>
        </div>
      </div>
      <div
        class="relative flex flex-row"
        :class="
          props.data.product.requirements?.length % 2 == 0
            ? 'col-span-1 items-start sm:col-span-2'
            : 'col-span-1 items-end sm:col-span-1'
        "
      >
        <button
          data-menu-requirements
          @click="isRequirementsPopupOpen = !isRequirementsPopupOpen"
          class="btn btn-ghost flex flex-row items-end gap-x-1.5 px-4"
        >
          <Icon type="filled" name="add_box" class="text-xl"></Icon>
          <span class="whitespace-nowrap text-primary">
            {{ __('Add more requirements') }}</span
          >
        </button>
        <ProductRequirementsPopup
          :is-open="isRequirementsPopupOpen === true"
          :requirements="
            data.product.requirements.filter((req) => req.value == null)
          "
          @addRequirement="handleAddRequirement"
        />
      </div>

      <div
        class="col-span-1 flex w-full flex-row items-center justify-center gap-x-2 sm:col-span-2"
      >
        <button @click="handleCancel" class="btn btn-ghost">
          <span>{{ __('Cancel') }}</span>
        </button>
        <button
          @click="
            $emit('submitForm', {
              data: encodedFormData(data.product),
              product_id: data.product.id
            })
          "
          v-if="data.product.type === 'standalone'"
          class="btn btn-fill flex items-center justify-center gap-x-2"
          :disabled="isLoading"
        >
          <span>
            {{
              headingText === __('Edit Product') ? __('Update ') : __('Add ')
            }}</span
          >
          <Icon
            v-if="isLoading"
            class="animate-spin text-lg"
            name="progress_activity"
          ></Icon>
        </button>

        <button
          v-else
          class="btn btn-fill"
          @click="$emit('initialValidation', encodedFormData(data.product))"
        >
          <span>{{ __('Next') }}</span>
        </button>
      </div>
    </div>

    <ProductVariantsForm
      v-else="currentStep === 2"
      :data="data"
      :selectedAttributes="selectedAttributes"
      :errors="errors"
      :heading-text="headingText"
      :is-loading="isLoading"
      @submitForm="
        $emit('submitForm', {
          data: encodedFormData(data.product),
          product_id: data.product.id
        })
      "
      @cancel="currentStep = 1"
    ></ProductVariantsForm>
  </div>
</template>
<script setup="">
  import { __ } from '@/helpers';
  import { nextTick, onMounted, ref, watch, computed } from 'vue';
  import Input from '@/components/global/Form/Input.vue';
  import FormSingleselect from '@/components/global/Form/FormSingleselect.vue';
  import InputTextarea from '@/components/form/input-textarea/input-textarea.vue';
  import InputToggle from '@/components/form/input-toggle/input-toggle.vue';
  import Icon from '@/components/block/icon/icon.vue';
  import CandidateShopCartProgressBar from '@/pages/candidate/shop/components/CandidateShopCartProgressBar.vue';
  import axios from 'axios';
  import ProductRequirementsPopup from './components/ProductRequirementsPopup.vue';
  import ProductVariantsForm from './components/ProductVariantsForm.vue';
  import ProductMediaForm from './components/ProductMediaForm.vue';
  import ProductLocationForm from './components/ProductLocationForm.vue';

  const props = defineProps({
    data: {
      type: Object,
      default: ''
    },
    headingText: {
      type: String
    },
    errors: Object,
    hasInitialValidation: Boolean,
    isLoading: Boolean
  });

  const isRequirementsPopupOpen = ref(false);
  const initialProductType = ref(props.data.product.type);

  onMounted(async () => {
    await nextTick();
    setupClickOutsideHandler();
  });

  watch(
    () => props.data.product.category,
    (newCategory) => {
      if (newCategory?.id && newCategory?.id !== currentProductCategoryId.value)
        axios
          .get(
            route('redesign.company.shop.products.get_category_attributes', {
              category: newCategory?.id
            })
          )
          .then((response) => {
            currentProductCategoryId.value = newCategory?.id;
            props.data.product.category_attributes = response.data;
            props.data.product.deleted_variants_ids =
              props.data.product.variants.map((variant) => variant.id);
            props.data.product.variants = [];
            selectedAttributes.value = [];
          })
          .catch((error) => {
            console.error('Error fetching attributes:', error);
          });
    }
  );

  const currentProductCategoryId = ref(
    props.data?.product?.category ? props.data?.product?.category?.id : null
  );
  const currentStep = ref(1);

  const handleChange = (newDeliveryTypeValue) => {
    props.data.product.delivery_type =
      props.data.product.delivery_type === newDeliveryTypeValue
        ? null
        : newDeliveryTypeValue;
  };

  const handleAddRequirement = async (event) => {
    isRequirementsPopupOpen.value = false;
    setTimeout(() => {
      props.data.product.requirements.find((req) => req.id === event.id).value =
        '';
    }, 100);
  };
  // Track which attributes are selected for variants (color, size, etc.)
  const selectedAttributes = ref(props.data.product.selectedAttributes || []);

  let clickOutsideHandler = null;
  const setupClickOutsideHandler = () => {
    clickOutsideHandler = (e) => {
      if (!e.target.closest('[data-menu-requirements]')) {
        isRequirementsPopupOpen.value = null;
      }
    };
    document.addEventListener('click', clickOutsideHandler);
  };

  const encodedFormData = (data) => {
    let uploadedIndex = 0;

    if (
      data.type === 'standalone' &&
      initialProductType.value === 'configurable'
    ) {
      data.variants = [];
    }
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (key === 'uploaded_images' && Array.isArray(value)) {
        value.forEach((image, index) => {
          if (image.file) {
            formData.append(`uploaded_images[${uploadedIndex}]`, image.file);
            formData.append(
              `uploaded_images_is_primary[${uploadedIndex}]`,
              image.is_primary
            );
            uploadedIndex++;
          }
        });
      } else if (value !== null && value !== undefined) {
        formData.append(key, JSON.stringify(value));
      }
    });

    return formData;
  };

  watch(
    () => props.hasInitialValidation,
    (newVal) => {
      newVal === true ? (currentStep.value = 2) : (currentStep.value = 1);
    }
  );

  const handleConfigurableButtonClick = () => {
    if (props.data.product.type === 'configurable') {
      return;
    } else {
      if (initialProductType.value === 'standalone') {
        props.data.product.variants = [];
        selectedAttributes.value = [];
      }
      props.data.product.type = 'configurable';
    }
  };

  const handleStandaloneButtonClick = () => {
    if (props.data.product.type === 'standalone') {
      return;
    } else {
      props.data.product.type = 'standalone';
    }
  };

  const handleInternalButtonClick = () => {
    if (
      props.data.product.internal === 1 &&
      props.data.product.external === 0
    ) {
      return;
    } else {
      props.data.product.internal = 1;
      props.data.product.external = 0;
    }
  };

  const handleExternalButtonClick = () => {
    if (
      props.data.product.external === 1 &&
      props.data.product.internal === 0
    ) {
      return;
    } else {
      props.data.product.external = 1;
      props.data.product.internal = 0;
    }
  };

  const handleBothButtonClick = () => {
    if (
      props.data.product.internal === 1 &&
      props.data.product.external === 1
    ) {
      return;
    } else {
      props.data.product.internal = 1;
      props.data.product.external = 1;
    }
  };

  const handleLocationRestrictionChange = () => {
    props.data.product.is_location_restricted =
      !props.data.product.is_location_restricted;
    props.data.product.locations.length === 0
      ? props.data.product.locations.push({
          tempId: Date.now() + Math.random()
        })
      : (props.data.product.locations = []);
  };

  const handleCancel = () => {
    window.history.back();
  };
</script>
